import React, {  useEffect } from "react";
import Card from "@albertsons/uds/molecule/Card";
import Button from "@albertsons/uds/molecule/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import { CheckCircle2, File, Trash2 } from "lucide-react";
import "./FileUpload.css";
import Tooltip from "@albertsons/uds/molecule/Tooltip";
import { useToast } from "../layout/Layout";

interface UploadFileCardProps {
  isTableVisible: boolean;
  inputRefUpload: React.RefObject<HTMLInputElement | null>;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  onDrop: (e: React.DragEvent) => void;
  tabType?: string;
  alertMessageUpload: string | null;
  handleCloseSnackbarUpload: (
    event: React.SyntheticEvent | Event,
    reason?: string
  ) => void;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  alertSeverityUpload: "success" | "error" | "warning" | "info";
  allowedFileTypes: string;
  filesFillTable: File[];
  setAlertMessageUpload: (message: string) => void;
  rows: any[];
  setAlertMessage: (message: string) => void;
  handleCloseSnackbar: () => void;
  isLoading: boolean;
  isUploading: boolean;
  setAlertSeverity: (
    severity: "success" | "error" | "warning" | "info"
  ) => void;
  setAlertSeverityUpload: (severity: "success" | "error") => void;
  onDeleteFile: (file: File) => void;
  resetValues: () => void;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onValidateClick: () => void;
  onUploadClick: () => void;
  isFileValidated: boolean;
  setIsFileValidated?: (value: boolean) => void;
  alertMessage: string | null;
  onUploadFilTable: (e: React.ChangeEvent<HTMLInputElement>) => void;
  alertSeverity: "success" | "error" | "warning" | "info";
  props: any;
  formatFileSize: (size: number) => string;
  showConfirmDialog: boolean;
  uploadResponse: any;
  onProceed: () => void;
  onCancel: () => void;
}

const UploadFileCard: React.FC<UploadFileCardProps> = ({
  isTableVisible,
  inputRefUpload,
  files,
  onDrop,
  tabType,
  alertMessageUpload,
  handleCloseSnackbarUpload,
  alertSeverityUpload,
  isOpen,
  setOpen,
  isLoading,
  isUploading,
  handleCloseSnackbar,
  onValidateClick,
  onUploadClick,
  allowedFileTypes,
  filesFillTable,
  setAlertMessageUpload,
  setAlertSeverityUpload,
  onDeleteFile,
  resetValues,
  onUpload,
  rows,
  alertMessage,
  isFileValidated,
  setIsFileValidated,
  alertSeverity,
  formatFileSize,
  props,
  showConfirmDialog,
  uploadResponse,
  onProceed,
  onCancel,
}) => {
  
  const { showError } = useToast();
  
  // Debug: Log when dialog state changes
  useEffect(() => {
  }, [showConfirmDialog]);
  

  // Handle user confirmation to proceed despite wave running
  const handleProceed = () => {
    onProceed();
  };

  // Handle user cancellation
  const handleCancel = () => {
    onCancel();
  };
  
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      <Card
        collapsible
        title="Upload File"
        isOpen={isOpen}
        onChange={setOpen}
        className="min-w-[600px]"
      >
        <Card.Header>
          <div className="flex items-center justify-end w-full"></div>
        </Card.Header>

        <Card.Content>
        <div>
          <div>
            <label style={{ marginRight: "20px", fontWeight: "bold" }}>
              Upload {tabType || 'XDOCK'}File*:
            </label>
          </div>
          <input
            type="file"
            ref={inputRefUpload}
            hidden
            onChange={(e) => onUpload(e)}
            multiple
            accept={allowedFileTypes}
          />
          <div
            className="browseAndButtonsContainer"
            style={{ display: "flex", alignItems: "flex-start" }}
          >
            <div
              className="flex flex-col items-center justify-center h-[130px] w-[450px] border-2 rounded border-dashed border-brand-light bg-blue-307"
              onDragOver={onDragOver}
              onDragEnter={onDragEnter}
              onDrop={(e) => onDrop(e)}
            >
              <div className="flex"></div>
              <p className="p-2.5 text-base leading-5 font-normal text-light-text">
                Drag and drop file or{" "}
                <button
                  onClick={() => {
                    if (
                      filesFillTable.length > 0 ||
                      rows.some((row) =>
                        Object.entries(row).some(
                          ([key, value]) =>
                            key !== "tempKey" && value !== "" && value !== null
                        )
                      )
                    ) {
                      showError("Please finish the fill/edit table task before proceeding with the Upload File operation");
                      return; // Prevent further execution
                    }
                    inputRefUpload?.current?.click();
                  }}
                  className="text-brand cursor-pointer underline"
                >
                  browse
                </button>
              </p>
            </div>
            <div className="buttonContainer">
              <div className="clearButton">
                <Button
                  width={120}
                  disabled={files.length === 0 || isLoading || isUploading}
                  onClick={resetValues}
                >
                  Clear
                </Button>
              </div>
              <div className="validateButton">
                <Button
                  width={120}
                  disabled={
                    !props.selectedDC.startsWith("W") ||
                    files.length === 0 ||
                    isLoading ||
                    isUploading
                  }
                  onClick={onValidateClick}
                >
                  Validate
                </Button>
                {isLoading && files.length > 0 && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}
              </div>
              <div className="uploadButton">
                <Button
                  width={120}
                  disabled={
                    !isFileValidated ||
                    files.length === 0 ||
                    isLoading ||
                    isUploading
                  }
                  onClick={onUploadClick}
                >
                  Upload to MA
                </Button>
                {isUploading && files.length > 0 && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 1000,
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <p className="h-full mt-1 italic text-sm leading-5 font-normal text-light-text">
          Only CSV files are allowed. Other file formats will be rejected.
        </p>
        {files.map((file) => (
          <div
            key={file.name}
            className="h-[130px] w-[450px] box-border border border-[#C8DAEB]  rounded-lg bg-white mt-2.5"
          >
            <div className="flex mt-2.5 ml-2.5">
              <div className="flex items-center justify-center w-[50px] h-[50px] bg-blue-307 rounded-full">
                <File color="#5A697B" />
              </div>
              {file.name.length > 30 ? (
                <Tooltip zIndex={10} anchor="right" label={file.name}>
                  <span
                    className="mt-2.5 ml-2.5 text-[16px] font-normal whitespace-nowrap cursor-pointer"
                  >
                    {file.name.slice(0, 30) + "..."}
                  </span>
                </Tooltip>
              ) : (
                <span className="mt-2.5 ml-2.5 text-[16px] font-normal whitespace-nowrap">
                  {file.name}
                </span>
              )}
            </div>
            <span className="ml-[68px] relative -top-[17px] text-[12px] text-[#5A697B]">
              {formatFileSize(file.size)}
            </span>
            <div className="flex float-right -mt-[34px]">
              <CheckCircle2 color="#1BA418" width="20px" height="20px" />
              <Trash2
                color="#5A697B"
                width="18px"
                height="20px"
                className="ml-2 mr-2"
                onClick={(e) => onDeleteFile(file)}
              />
            </div>
          </div>
        ))}
      </Card.Content>
    </Card>


    {/* Confirmation Dialog for Wave Running */}
    <Dialog
      open={showConfirmDialog}
      onClose={handleCancel}
      aria-labelledby="wave-confirm-dialog-title"
      aria-describedby="wave-confirm-dialog-description"
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          zIndex: 9999,
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
      style={{ zIndex: 9999 }}
    >
      <DialogTitle 
        id="wave-confirm-dialog-title"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '20px 24px',
          fontSize: '20px',
          fontWeight: '600',
          letterSpacing: '0.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <span style={{
          fontSize: '24px',
          animation: 'pulse 2s infinite'
        }}>⚠️</span>
        Wave is Currently Running
      </DialogTitle>
      <DialogContent style={{ padding: '24px' }}>
        <DialogContentText 
          id="wave-confirm-dialog-description" 
          style={{ 
            marginBottom: '20px',
            fontSize: '15px',
            color: '#424242',
            lineHeight: '1.6',
            backgroundColor: '#fff3e0',
            padding: '16px',
            borderRadius: '8px',
            borderLeft: '4px solid #ff9800',
            fontWeight: '500'
          }}
        >
          <span style={{ fontSize: '18px', marginRight: '8px' }}>⚡</span>
          Below waves are currently running. Do you want to proceed with the upload?
        </DialogContentText>
        
        {/* Display upload data in a table */}
        {uploadResponse && uploadResponse.waveStatusApiResponseList && uploadResponse.waveStatusApiResponseList.length > 0 && (
          <div style={{ 
            overflowX: 'auto', 
            overflowY: 'auto',
            maxHeight: '400px',
            marginTop: '16px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}>
                  <th style={{ 
                    padding: '10px 8px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    borderRight: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    Order Planning Run ID
                  </th>
                  <th style={{ 
                    padding: '10px 8px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    borderRight: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    Description
                  </th>
                  <th style={{ 
                    padding: '10px 8px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    borderRight: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    Created By
                  </th>
                  <th style={{ 
                    padding: '10px 8px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '12px',
                    letterSpacing: '0.5px'
                  }}>
                    Created Timestamp
                  </th>
                  <th style={{ 
                    padding: '10px 8px', 
                    textAlign: 'left', 
                    fontWeight: '600',
                    fontSize: '12px',
                    letterSpacing: '0.5px',
                    borderRight: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    Time Elapsed
                  </th>
                </tr>
              </thead>
              <tbody>
                {uploadResponse.waveStatusApiResponseList.map((item: any, index: number) => (
                  <tr 
                    key={index} 
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      transition: 'background-color 0.2s',
                      cursor: 'default'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa'}
                  >
                    <td style={{ 
                      padding: '8px', 
                      borderBottom: '1px solid #e0e0e0',
                      color: '#424242'
                    }}>
                      {item.OrderPlanningRunId}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      borderBottom: '1px solid #e0e0e0',
                      color: '#616161',
                      maxWidth: '250px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} title={item.Description}>
                      {item.Description}
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      borderBottom: '1px solid #e0e0e0',
                      color: '#424242'
                    }}>
                      <span style={{
                        backgroundColor: '#e8eaf6',
                        padding: '3px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        color: '#3f51b5'
                      }}>
                        {item.CreatedBy}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '8px', 
                      borderBottom: '1px solid #e0e0e0',
                      color: '#757575',
                      fontSize: '12px'
                    }}>
                      {new Date(item.CreatedTimestamp).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                      {/* Time Elapsed column */}
                      <td style={{
                        padding: '8px',
                        borderBottom: '1px solid #e0e0e0',
                        color: '#424242'
                      }}>
                        {item.timeElapsed}
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
      <DialogActions style={{ 
        padding: '16px 24px', 
        backgroundColor: '#f5f5f5',
        gap: '12px',
        justifyContent: 'flex-end'
      }}>
        <Button 
          onClick={handleCancel} 
          variant="secondary"
          width={120}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleProceed} 
          variant="primary"
          width={120}
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default UploadFileCard;